import {
  Badge,
  Box,
  Button,
  Card,
  CloseButton,
  Dialog,
  Heading,
  HStack,
  Image,
  Portal,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  Activity,
  ActivityIcon,
  AlertTriangleIcon,
  ArrowRight,
  Brain,
  Check,
  ClipboardListIcon,
  Cpu,
  CpuIcon,
  Database,
  DatabaseIcon,
  FileTextIcon,
  GaugeIcon,
  GitBranchIcon,
  Globe,
  Layers,
  LockIcon,
  type LucideIcon,
  Network,
  NetworkIcon,
  RadioIcon,
  RefreshCw,
  SearchIcon,
  Server,
  ServerIcon,
  Share2Icon,
  Shield,
  ShieldCheckIcon,
  Workflow,
  Zap,
  ZoomIn,
} from 'lucide-react'
import { useState } from 'react'

import hivemqLogo from '@/assets/hivemq-bee-neg.svg'
import pulseSchema from '@/assets/pulse/DistributedIntelligenceSchema.png'
import pulseAnimation from '@/assets/pulse/pulse-animation.gif'
import { InsightCard, PageHeader, RevealStack, SectionHeading } from '@/components/shell'

export const Route = createFileRoute('/_app/pulse')({
  component: PulsePage,
})

interface ZoomableMediaProps {
  src: string
  alt: string
  maxH?: string
  isVideo?: boolean
  onZoom: (src: string, alt: string, isVideo?: boolean) => void
}

function ZoomableMedia({ src, alt, maxH = '400px', isVideo = false, onZoom }: ZoomableMediaProps) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: pre-existing pattern predating this restyle; already keyboard/aria accessible below
    <Box
      position="relative"
      cursor="zoom-in"
      onClick={() => onZoom(src, alt, isVideo)}
      role="button"
      tabIndex={0}
      aria-label={`Zoom into ${alt}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onZoom(src, alt, isVideo)
        }
      }}
      _hover={{
        '& > .zoom-overlay': {
          opacity: 1,
        },
      }}
    >
      {isVideo ? (
        <video
          src={src}
          autoPlay
          loop
          muted
          playsInline
          style={{ maxHeight: maxH, objectFit: 'contain', borderRadius: '8px' }}
        />
      ) : (
        <Image src={src} alt={alt} maxH={maxH} objectFit="contain" borderRadius="lg" />
      )}
      <Box
        className="zoom-overlay"
        position="absolute"
        top={2}
        right={2}
        bg="blackAlpha.600"
        borderRadius="md"
        p={2}
        opacity={0}
        transition="opacity 0.2s"
      >
        <ZoomIn size={20} color="white" />
      </Box>
    </Box>
  )
}

interface FeatureItemProps {
  title: string
  description: string
}

function FeatureItem({ title, description }: FeatureItemProps) {
  return (
    <HStack gap={3} align="flex-start">
      <Box p={1} bg="green.100" borderRadius="full" flexShrink={0} mt={1}>
        <Check size={14} color="var(--chakra-colors-green-600)" />
      </Box>
      <VStack align="start" gap={0}>
        <Text fontWeight="bold">{title}</Text>
        <Text fontSize="sm" color="content.secondary">
          {description}
        </Text>
      </VStack>
    </HStack>
  )
}

interface FeatureCardProps {
  icon: typeof Shield
  title: string
  description: string
}

function FeatureCard({ icon: Icon, title, description }: FeatureCardProps) {
  return (
    <Card.Root transition="all 0.3s" _hover={{ transform: 'translateY(-4px)', shadow: 'lg' }}>
      <Card.Body p={5}>
        <VStack align="start" gap={3}>
          <Box p={2} bg="brand.solid" borderRadius="lg">
            <Icon size={20} />
          </Box>
          <VStack align="start" gap={1}>
            <Text fontWeight="bold">{title}</Text>
            <Text fontSize="sm" color="content.secondary">
              {description}
            </Text>
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

interface ComponentCardProps {
  icon: typeof Server
  title: string
  subtitle: string
  description: string
  features: string[]
  color: string
}

function ComponentCard({
  icon: Icon,
  title,
  subtitle,
  description,
  features,
  color,
}: ComponentCardProps) {
  return (
    <Card.Root transition="all 0.3s" _hover={{ transform: 'translateY(-6px)', shadow: 'xl' }}>
      <Card.Header p={5} pb={3}>
        <HStack gap={3}>
          <Box p={3} bg={color} borderRadius="lg">
            <Icon size={24} color="white" />
          </Box>
          <VStack align="start" gap={0}>
            <Heading size="md">{title}</Heading>
            <Text fontSize="sm" color="content.secondary">
              {subtitle}
            </Text>
          </VStack>
        </HStack>
      </Card.Header>
      <Card.Body p={5} pt={0}>
        <VStack align="start" gap={3}>
          <Text fontSize="sm" color="content.secondary">
            {description}
          </Text>
          <VStack align="start" gap={2} w="100%">
            {features.map((feature) => (
              <HStack key={feature} gap={2}>
                <Box p={0.5} bg="green.100" borderRadius="full">
                  <Check size={12} color="var(--chakra-colors-green-600)" />
                </Box>
                <Text fontSize="sm">{feature}</Text>
              </HStack>
            ))}
          </VStack>
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}

interface InsightItem {
  accent: string
  colorPalette: string
  icon: LucideIcon
  title: string
  description: string
  meta?: string
}

const intelligenceLayerCapabilities: InsightItem[] = [
  {
    accent: 'blue.solid',
    colorPalette: 'blue',
    icon: NetworkIcon,
    title: 'A governed namespace',
    description: 'Discover, model, and validate operational data in one place.',
  },
  {
    accent: 'purple.solid',
    colorPalette: 'purple',
    icon: RadioIcon,
    title: 'Native MQTT, end to end',
    description: 'Lives where your data is published. No new ingest layer.',
  },
  {
    accent: 'green.solid',
    colorPalette: 'green',
    icon: CpuIcon,
    title: 'Edge-resident',
    description: 'Payloads stay on your floor. Summaries and metadata flow up.',
  },
  {
    accent: 'orange.solid',
    colorPalette: 'orange',
    icon: ShieldCheckIcon,
    title: 'Validated at publish',
    description: 'Schema enforcement at the moment of publication, not in cleanup.',
  },
  {
    accent: 'teal.solid',
    colorPalette: 'teal',
    icon: ActivityIcon,
    title: 'Live KPIs',
    description:
      'A typed expression language over governed topics. Defined once, evaluated continuously.',
  },
  {
    accent: 'cyan.solid',
    colorPalette: 'cyan',
    icon: FileTextIcon,
    title: 'Auditable by design',
    description: 'Lineage, traceability, and trust scoring for regulated operations.',
  },
]

const knowledgeGraphViews = [
  { icon: NetworkIcon, label: 'Namespace (hierarchical view)' },
  { icon: SearchIcon, label: 'Typed-search' },
  { icon: Share2Icon, label: 'Graph query' },
  { icon: GaugeIcon, label: 'Computed & derived views' },
]

const governanceEnginePoints: InsightItem[] = [
  {
    accent: 'blue.solid',
    colorPalette: 'blue',
    icon: Check,
    title: 'Validated data flows downstream',
    description: 'No cleanup step.',
  },
  {
    accent: 'orange.solid',
    colorPalette: 'orange',
    icon: AlertTriangleIcon,
    title: 'Anomalies and schema violations are caught at the source',
    description: 'Deviations are flagged the moment they are published.',
  },
  {
    accent: 'purple.solid',
    colorPalette: 'purple',
    icon: SearchIcon,
    title: 'Ungoverned topics are surfaced for inclusion in the model',
    description: 'Nothing stays invisible to the namespace for long.',
  },
  {
    accent: 'teal.solid',
    colorPalette: 'teal',
    icon: GitBranchIcon,
    title: 'Every classification informs the shape of the model',
    description: 'The namespace learns from what it sees.',
  },
]

const governedNamespaceCapabilities: InsightItem[] = [
  {
    accent: 'blue.solid',
    colorPalette: 'blue',
    icon: LockIcon,
    title: 'Schema enforcement at publish',
    description:
      'Bad data never enters the namespace. Validation at the moment of write, not a downstream cleanup step.',
  },
  {
    accent: 'orange.solid',
    colorPalette: 'orange',
    icon: AlertTriangleIcon,
    title: 'Deviation & anomaly detection',
    description:
      'Schema violations, type mismatches, range outliers, payload pattern anomalies - caught at the source.',
  },
  {
    accent: 'green.solid',
    colorPalette: 'green',
    icon: GaugeIcon,
    title: 'Computed KPIs from live streams',
    description:
      'A typed expression language over governed topics: moving averages, aggregates, derived state - defined once.',
  },
  {
    accent: 'purple.solid',
    colorPalette: 'purple',
    icon: ClipboardListIcon,
    title: 'Auditable governance state',
    description:
      'Every classification logged; a governance overview by time and by agent instance.',
  },
]

const regulatedOpsCapabilities: InsightItem[] = [
  {
    accent: 'green.solid',
    colorPalette: 'green',
    icon: ShieldCheckIcon,
    title: 'Schema enforcement at publish',
    description: 'Bad data is rejected at the moment of write.',
    meta: 'TODAY',
  },
  {
    accent: 'green.solid',
    colorPalette: 'green',
    icon: FileTextIcon,
    title: 'Governed/ungoverned classification with reporting',
    description: 'Every datapoint classified and recorded.',
    meta: 'TODAY',
  },
  {
    accent: 'blue.solid',
    colorPalette: 'blue',
    icon: LockIcon,
    title: 'Role-based namespace access (RBAC)',
    description: 'Per-role read, write, and govern permissions.',
    meta: 'H2 2026',
  },
  {
    accent: 'purple.solid',
    colorPalette: 'purple',
    icon: GitBranchIcon,
    title: 'Lineage & traceability',
    description: 'See where any value came from, what transforms it, and where it lands.',
    meta: 'Roadmap',
  },
  {
    accent: 'purple.solid',
    colorPalette: 'purple',
    icon: GaugeIcon,
    title: 'Trust scoring on operational data',
    description: 'A score per record; feeds governance and downstream agents.',
    meta: 'Roadmap',
  },
]

function PulsePage() {
  const navigate = useNavigate()
  const [zoomMedia, setZoomMedia] = useState<{
    src: string
    alt: string
    isVideo?: boolean
  } | null>(null)

  const handleZoom = (src: string, alt: string, isVideo?: boolean) => {
    setZoomMedia({ src, alt, isVideo })
  }

  const handleCloseZoom = () => {
    setZoomMedia(null)
  }

  const coreFeatures = [
    {
      icon: Network,
      title: 'Unified Namespace (UNS)',
      description:
        'Build a single, semantic data layer that provides consistent access to all operational data across your organization.',
    },
    {
      icon: Zap,
      title: 'Real-time Processing',
      description:
        'Process and transform data at the edge with ultra-low latency, enabling immediate insights and actions.',
    },
    {
      icon: Layers,
      title: 'Contextual Enrichment',
      description:
        'Automatically add context to raw data streams with metadata, transformations, and semantic meaning.',
    },
    {
      icon: Workflow,
      title: 'Data Pipelines',
      description:
        'Create sophisticated data flows between systems with filtering, transformation, and routing logic.',
    },
    {
      icon: Brain,
      title: 'AI/ML Ready',
      description:
        'Prepare and deliver clean, structured data streams ready for machine learning and AI model consumption.',
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description:
        'Secure data at rest and in transit with TLS encryption, authentication, and fine-grained access control.',
    },
  ]

  const components = [
    {
      icon: Server,
      title: 'Pulse Server',
      subtitle: 'Central Coordination Hub',
      description:
        'The brain of your distributed data intelligence platform. Pulse Server manages configuration, coordinates agents, and provides centralized monitoring and control.',
      features: [
        'Agent lifecycle management',
        'Centralized configuration',
        'Real-time monitoring dashboard',
        'High availability clustering',
      ],
      color: 'info.solid',
    },
    {
      icon: Cpu,
      title: 'Pulse Agent',
      subtitle: 'Edge Intelligence',
      description:
        'Lightweight agents deployed at the edge to collect, process, and transform data close to its source. Run complex pipelines without cloud round-trips.',
      features: [
        'Local data processing',
        'Protocol adapters (OPC-UA, Modbus, MQTT)',
        'Offline operation support',
        'Lightweight footprint',
      ],
      color: 'success.solid',
    },
    {
      icon: Globe,
      title: 'Pulse Client',
      subtitle: 'JavaScript Library',
      description:
        'A powerful JavaScript library for building web applications and dashboards that consume real-time data streams directly from the Pulse platform.',
      features: [
        'React, Vue, Angular support',
        'WebSocket connectivity',
        'Automatic reconnection',
        'TypeScript definitions',
      ],
      color: 'highlight.solid',
    },
  ]

  const useCases = [
    {
      icon: Activity,
      title: 'Predictive Maintenance',
      description:
        'Analyze equipment sensor data in real-time to predict failures before they happen, reducing downtime and maintenance costs.',
    },
    {
      icon: Database,
      title: 'Data Historian Replacement',
      description:
        'Modern, scalable alternative to traditional historians with real-time querying and seamless cloud integration.',
    },
    {
      icon: RefreshCw,
      title: 'IT/OT Convergence',
      description:
        'Bridge the gap between operational technology and information technology with a unified data platform.',
    },
  ]

  return (
    <Box maxW="1400px" mx="auto">
      <PageHeader
        centered
        title="Data Intelligence"
        subtitle="The distributed data intelligence layer of the HiveMQ platform, powered by HiveMQ Pulse. Build your Unified Namespace, govern it at publish, and power real-time insights across your enterprise."
      />

      <RevealStack align="stretch" gap={10}>
        {/* Hero identity */}
        <VStack gap={5} textAlign="center">
          <Image src={hivemqLogo} alt="HiveMQ" h="90px" objectFit="contain" />
          <HStack gap={2} flexWrap="wrap" justify="center">
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                Unified Namespace
              </Text>
            </Box>
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                Edge Processing
              </Text>
            </Box>
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                Data Pipelines
              </Text>
            </Box>
            <Box px={3} py={1} bg="brand.solid" borderRadius="full">
              <Text fontSize="sm" fontWeight="medium" color="gray.900">
                AI-Ready Data
              </Text>
            </Box>
          </HStack>
        </VStack>

        {/* Architecture Animation */}
        <Card.Root overflow="hidden">
          <Card.Body p={6}>
            <Box display="flex" justifyContent="center">
              <ZoomableMedia
                src={pulseAnimation}
                alt="HiveMQ Pulse Architecture"
                maxH="400px"
                onZoom={handleZoom}
              />
            </Box>
          </Card.Body>
        </Card.Root>

        {/* What is Pulse */}
        <VStack align="stretch" gap={4}>
          <SectionHeading centered title="What is HiveMQ Pulse?" />

          <Card.Root w="100%">
            <Card.Body p={6}>
              <VStack align="start" gap={4}>
                <Text fontSize="md">
                  <strong>HiveMQ Pulse</strong> is a{' '}
                  <strong>Distributed Data Intelligence Platform</strong> designed to help
                  organizations build and manage their <strong>Unified Namespace (UNS)</strong> - a
                  single, semantic layer that provides real-time access to all operational data.
                </Text>
                <Text fontSize="md">
                  Unlike traditional point-to-point integrations, Pulse creates a{' '}
                  <strong>centralized data fabric</strong> where any system can publish or subscribe
                  to data without needing to know about other systems. This
                  <strong> decoupled architecture</strong> dramatically simplifies enterprise
                  integration.
                </Text>
                <Text fontSize="md">
                  With <strong>edge processing capabilities</strong>, Pulse Agents can transform,
                  filter, and enrich data at the source - reducing bandwidth, latency, and cloud
                  costs while enabling
                  <strong> real-time decision making</strong> even in air-gapped environments.
                </Text>
                <Text fontSize="md">
                  Pulse integrates seamlessly with the <strong>HiveMQ MQTT Platform</strong>,
                  extending its capabilities with sophisticated data management, contextual
                  enrichment, and enterprise-grade UNS building blocks.
                </Text>
                <Text fontSize="md">
                  In the HiveMQ Industrial Data Platform, Pulse is the{' '}
                  <strong>Data Intelligence layer</strong>: it sits between data streaming (broker +
                  edge) and agentic AI, turning raw operational data into something governed,
                  trustworthy, and ready to act on.
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* An Intelligence Layer for Industrial Operations */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="An Intelligence Layer for Industrial Operations"
            description="A single model describes what your data means. The platform enforces it at every layer."
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
            {intelligenceLayerCapabilities.map((capability) => (
              <InsightCard
                key={capability.title}
                accent={capability.accent}
                colorPalette={capability.colorPalette}
                icon={capability.icon}
                badgeLabel="Capability"
                title={capability.title}
                description={capability.description}
              />
            ))}
          </SimpleGrid>
        </VStack>

        {/* The Knowledge Graph */}
        <VStack align="stretch" gap={4}>
          <SectionHeading centered title="The Knowledge Graph" />
          <Card.Root w="100%">
            <Card.Body p={6}>
              <VStack align="start" gap={4}>
                <HStack gap={3}>
                  <Box p={2} bg="brand.solid" borderRadius="lg">
                    <Share2Icon size={20} />
                  </Box>
                  <VStack align="start" gap={0}>
                    <Badge colorPalette="brand" variant="subtle">
                      Model
                    </Badge>
                    <Heading size="md">A model of your operational data</Heading>
                  </VStack>
                </HStack>
                <Text fontSize="md" color="content.secondary">
                  The namespace is one view onto it. Nodes, relations, and types hold it all
                  together.
                </Text>
                <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} gap={3} w="100%">
                  {knowledgeGraphViews.map((view) => (
                    <HStack key={view.label} gap={2} px={3} py={2} bg="bg.subtle" borderRadius="lg">
                      <view.icon size={16} />
                      <Text fontSize="sm" fontWeight="medium">
                        {view.label}
                      </Text>
                    </HStack>
                  ))}
                </SimpleGrid>
                <Text fontSize="sm" color="content.tertiary" fontStyle="italic">
                  One model. Many ways to browse, query, and operate on it.
                </Text>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* The Governance Engine */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="The Governance Engine"
            description="Every published datapoint is classified, then routed."
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
            {governanceEnginePoints.map((point) => (
              <InsightCard
                key={point.title}
                accent={point.accent}
                colorPalette={point.colorPalette}
                icon={point.icon}
                badgeLabel="Governance"
                title={point.title}
                description={point.description}
              />
            ))}
          </SimpleGrid>
        </VStack>

        {/* What a Governed Namespace Lets You Do */}
        <VStack align="stretch" gap={4}>
          <SectionHeading centered title="What a Governed Namespace Lets You Do" />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} gap={6}>
            {governedNamespaceCapabilities.map((capability) => (
              <InsightCard
                key={capability.title}
                accent={capability.accent}
                colorPalette={capability.colorPalette}
                icon={capability.icon}
                badgeLabel="Capability"
                title={capability.title}
                description={capability.description}
              />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Architecture: Agents + Server */}
        <VStack align="stretch" gap={4}>
          <SectionHeading centered title="Architecture: Agents + Server" />
          <Card.Root bg="bg.subtle">
            <Card.Body p={6}>
              <VStack align="start" gap={4}>
                <HStack gap={3}>
                  <Box p={2} bg="brand.solid" borderRadius="lg">
                    <ServerIcon size={20} />
                  </Box>
                  <VStack align="start" gap={0}>
                    <Badge colorPalette="brand" variant="subtle">
                      Architecture
                    </Badge>
                    <Heading size="md">Hybrid by design</Heading>
                  </VStack>
                </HStack>
                <Text fontSize="sm" color="content.secondary">
                  Your data stays on your floor; your control plane is centralized. Original
                  payloads stay where they are produced - only metadata, classifications, and
                  governance state cross the seam.
                </Text>
                <VStack align="start" gap={3} w="100%">
                  <FeatureItem
                    title="Pulse Agent"
                    description="Runs at the edge, alongside your data sources. Payloads never leave your floor."
                  />
                  <FeatureItem
                    title="Pulse Server"
                    description="Centralized control plane: configuration, governance state, and coordination."
                  />
                  <FeatureItem
                    title="One outbound port, TLS"
                    description="No inbound exposure of your network."
                  />
                </VStack>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* Core Features */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Core Features"
            description="Everything you need to build a modern, intelligent data infrastructure"
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
            {coreFeatures.map((feature) => (
              <FeatureCard key={feature.title} {...feature} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Architecture Components */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Platform Components"
            description="A distributed architecture designed for enterprise scale and edge deployment"
          />
          <SimpleGrid columns={{ base: 1, lg: 3 }} gap={6}>
            {components.map((component) => (
              <ComponentCard key={component.title} {...component} />
            ))}
          </SimpleGrid>

          {/* Schema Diagram */}
          <Card.Root w="100%" overflow="hidden">
            <Card.Body p={6}>
              <Box display="flex" justifyContent="center">
                <ZoomableMedia
                  src={pulseSchema}
                  alt="Distributed Intelligence Schema"
                  maxH="350px"
                  onZoom={handleZoom}
                />
              </Box>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* How It Works */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Building Your UNS with Pulse"
            description="From raw data to actionable intelligence in real-time"
          />
          <SimpleGrid columns={{ base: 1, lg: 2 }} gap={8} alignItems="center">
            <Card.Root>
              <Card.Body p={6}>
                <VStack align="start" gap={4}>
                  <FeatureItem
                    title="1. Deploy Pulse Agents"
                    description="Install lightweight agents at edge locations, connected devices, or data centers."
                  />
                  <FeatureItem
                    title="2. Configure Data Sources"
                    description="Connect to OPC-UA servers, Modbus devices, databases, APIs, and more."
                  />
                  <FeatureItem
                    title="3. Define Data Models"
                    description="Create semantic models that give meaning and context to raw data streams."
                  />
                  <FeatureItem
                    title="4. Build Pipelines"
                    description="Process, transform, and route data with powerful pipeline definitions."
                  />
                  <FeatureItem
                    title="5. Publish to UNS"
                    description="Deliver clean, enriched data to your Unified Namespace via MQTT."
                  />
                  <FeatureItem
                    title="6. Consume Anywhere"
                    description="Applications, dashboards, and AI models subscribe to the data they need."
                  />
                </VStack>
              </Card.Body>
            </Card.Root>

            <Card.Root bg="bg.subtle">
              <Card.Body p={6}>
                <VStack gap={4}>
                  <Heading size="md">Why Unified Namespace?</Heading>
                  <VStack align="start" gap={3} w="100%">
                    <HStack gap={3}>
                      <Box p={1} bg="green.100" borderRadius="full">
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                      </Box>
                      <Text fontSize="sm">Single source of truth for all operational data</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Box p={1} bg="green.100" borderRadius="full">
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                      </Box>
                      <Text fontSize="sm">Eliminates point-to-point integration complexity</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Box p={1} bg="green.100" borderRadius="full">
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                      </Box>
                      <Text fontSize="sm">Enables real-time data access for any application</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Box p={1} bg="green.100" borderRadius="full">
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                      </Box>
                      <Text fontSize="sm">Reduces integration costs by up to 80%</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Box p={1} bg="green.100" borderRadius="full">
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                      </Box>
                      <Text fontSize="sm">Accelerates digital transformation initiatives</Text>
                    </HStack>
                    <HStack gap={3}>
                      <Box p={1} bg="green.100" borderRadius="full">
                        <Check size={14} color="var(--chakra-colors-green-600)" />
                      </Box>
                      <Text fontSize="sm">Future-proofs your data infrastructure</Text>
                    </HStack>
                  </VStack>
                </VStack>
              </Card.Body>
            </Card.Root>
          </SimpleGrid>
        </VStack>

        {/* Use Cases */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Built for Industrial IoT"
            description="Pulse powers critical use cases across manufacturing, energy, and logistics"
          />
          <SimpleGrid columns={{ base: 1, md: 3 }} gap={4}>
            {useCases.map((useCase) => (
              <FeatureCard key={useCase.title} {...useCase} />
            ))}
          </SimpleGrid>
        </VStack>

        {/* Built for Regulated Operations */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Built for Regulated Operations"
            description="Governance state that a regulated operation can stand behind."
          />
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={6}>
            {regulatedOpsCapabilities.map((capability) => (
              <InsightCard
                key={capability.title}
                accent={capability.accent}
                colorPalette={capability.colorPalette}
                icon={capability.icon}
                badgeLabel="Governance"
                meta={capability.meta}
                title={capability.title}
                description={capability.description}
              />
            ))}
          </SimpleGrid>
          <Text fontSize="sm" color="content.tertiary" fontStyle="italic">
            Status as of May 2026; roadmap items target subsequent updates.
          </Text>
        </VStack>

        {/* Get started */}
        <VStack align="stretch" gap={4}>
          <SectionHeading
            centered
            title="Get Started"
            description="Data Intelligence is part of the HiveMQ Unified Platform."
          />
          <Card.Root
            bg="bg.subtle"
            borderWidth="1px"
            borderColor="border"
            borderLeftWidth="4px"
            borderLeftColor="brand.solid"
          >
            <Card.Body p={8}>
              <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
                <VStack align="start" gap={1}>
                  <Heading size="lg">HiveMQ Unified Platform</Heading>
                  <Text color="content.secondary">
                    Build your Unified Namespace and turn it into distributed data intelligence -
                    all in one platform.
                  </Text>
                </VStack>
                <Button size="lg" asChild>
                  <a href="https://app.hivemq.com" target="_blank" rel="noopener noreferrer">
                    Open the Unified Platform
                    <ArrowRight size={18} />
                  </a>
                </Button>
              </HStack>
            </Card.Body>
          </Card.Root>
        </VStack>

        {/* Part of the Unified Platform */}
        <Card.Root borderLeftWidth="4px" borderLeftColor="brand.solid" bg="bg.subtle">
          <Card.Body p={6}>
            <VStack align="start" gap={3}>
              <HStack gap={3}>
                <Box p={2} bg="brand.solid" borderRadius="lg">
                  <DatabaseIcon size={20} />
                </Box>
                <VStack align="start" gap={0}>
                  <Badge colorPalette="brand" variant="subtle">
                    Layer 2 · Data Intelligence
                  </Badge>
                  <Heading size="md">Part of the Unified Platform</Heading>
                </VStack>
              </HStack>
              <Text fontSize="sm" color="content.secondary">
                Pulse is the Data Intelligence layer of the HiveMQ Industrial Data Platform -
                between Data Streaming (broker + edge) below it and Agentic AI above it. Pulse makes
                operational data understandable and trustworthy; the layer above turns it into safe,
                autonomous action.
              </Text>
              <Button variant="outline" onClick={() => navigate({ to: '/hivemqagentic' })}>
                Explore Agentic AI
                <ArrowRight size={18} />
              </Button>
            </VStack>
          </Card.Body>
        </Card.Root>

        {/* CTA */}
        <Card.Root bg="bg.subtle">
          <Card.Body p={8}>
            <HStack justify="space-between" align="center" flexWrap="wrap" gap={4}>
              <VStack align="start" gap={1}>
                <Heading size="lg">From intelligence to action</Heading>
                <Text color="content.secondary">
                  See how governed data intelligence powers safe, closed-loop Agentic AI
                </Text>
              </VStack>
              <Button size="lg" onClick={() => navigate({ to: '/hivemqagentic' })}>
                Explore Agentic AI
                <ArrowRight size={18} />
              </Button>
            </HStack>
          </Card.Body>
        </Card.Root>
      </RevealStack>

      {/* Zoom Modal */}
      <Dialog.Root
        open={zoomMedia !== null}
        onOpenChange={(details) => {
          if (!details.open) {
            handleCloseZoom()
          }
        }}
        size="cover"
        placement="center"
        motionPreset="scale"
      >
        <Portal>
          <Dialog.Backdrop bg="blackAlpha.800" />
          <Dialog.Positioner>
            <Dialog.Content
              bg="transparent"
              shadow="none"
              maxW="95vw"
              maxH="95vh"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Dialog.CloseTrigger asChild position="absolute" top={4} right={4}>
                <CloseButton
                  size="lg"
                  bg="blackAlpha.600"
                  color="white"
                  _hover={{ bg: 'blackAlpha.800' }}
                  borderRadius="full"
                />
              </Dialog.CloseTrigger>
              <Dialog.Body
                p={0}
                display="flex"
                alignItems="center"
                justifyContent="center"
                onClick={handleCloseZoom}
              >
                {zoomMedia?.isVideo ? (
                  <video
                    src={zoomMedia.src}
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{
                      maxWidth: '90vw',
                      maxHeight: '90vh',
                      objectFit: 'contain',
                      borderRadius: '8px',
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <Image
                    src={zoomMedia?.src}
                    alt={zoomMedia?.alt || ''}
                    maxW="90vw"
                    maxH="90vh"
                    objectFit="contain"
                    borderRadius="lg"
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  )
}
